import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { ChartBlock } from '../../types';
import { formatChartDate } from '../../../../lib/utils';

interface ChartBlockRendererProps {
  block: ChartBlock;
}

const COLORS = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#ec4899',
  '#06b6d4',
];

export const ChartBlockRenderer: React.FC<ChartBlockRendererProps> = ({ block }) => {
  const { chart, title, x, series } = block;

  const data = useMemo(() => {
    return x.map((label, idx) => {
      const formattedLabel = formatChartDate(label);
      const entry: Record<string, any> = { xLabel: formattedLabel, rawX: label };
      series.forEach((s) => {
        entry[s.name] = s.data[idx] ?? 0;
      });
      return entry;
    });
  }, [x, series]);

  const hasMultipleSeries = series.length > 1;

  return (
    <div className="my-3 w-full bg-white rounded-xl border border-gray-200 shadow-sm p-4">
      {title && (
        <h4 className="text-xs font-semibold text-gray-800 uppercase tracking-wider mb-3">
          {title}
        </h4>
      )}

      <div className="w-full h-72">
        <ResponsiveContainer width="100%" height="100%">
          {chart === 'bar' ? (
            <BarChart
              data={data}
              margin={{ top: 10, right: 15, left: -10, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis
                dataKey="xLabel"
                tick={{ fontSize: 11, fill: '#64748b' }}
                tickLine={false}
                axisLine={{ stroke: '#e2e8f0' }}
                angle={0}
                textAnchor="middle"
                dy={4}
                interval={x.length > 7 ? 'preserveStartEnd' : 0}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#64748b' }}
                tickLine={false}
                axisLine={{ stroke: '#e2e8f0' }}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#ffffff',
                  borderColor: '#e2e8f0',
                  borderRadius: '0.5rem',
                  fontSize: '0.75rem',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                }}
              />
              {hasMultipleSeries && (
                <Legend wrapperStyle={{ fontSize: '0.75rem', paddingTop: '8px' }} />
              )}
              {series.map((s, idx) => (
                <Bar
                  key={s.name}
                  dataKey={s.name}
                  fill={COLORS[idx % COLORS.length]}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={45}
                />
              ))}
            </BarChart>
          ) : (
            <LineChart
              data={data}
              margin={{ top: 10, right: 15, left: -10, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis
                dataKey="xLabel"
                tick={{ fontSize: 11, fill: '#64748b' }}
                tickLine={false}
                axisLine={{ stroke: '#e2e8f0' }}
                angle={0}
                textAnchor="middle"
                dy={4}
                interval={x.length > 7 ? 'preserveStartEnd' : 0}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#64748b' }}
                tickLine={false}
                axisLine={{ stroke: '#e2e8f0' }}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#ffffff',
                  borderColor: '#e2e8f0',
                  borderRadius: '0.5rem',
                  fontSize: '0.75rem',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                }}
              />
              {hasMultipleSeries && (
                <Legend wrapperStyle={{ fontSize: '0.75rem', paddingTop: '8px' }} />
              )}
              {series.map((s, idx) => (
                <Line
                  key={s.name}
                  type="monotone"
                  dataKey={s.name}
                  stroke={COLORS[idx % COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 3, fill: COLORS[idx % COLORS.length] }}
                  activeDot={{ r: 5 }}
                />
              ))}
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};
